import ApolloClient, { isApolloError } from 'apollo-client';
import Bugsnag, { onReject } from 'common/utils/Bugsnag';
import ErrorPage from 'projects/routes/ErrorPage';
import React from 'react';
import { ApolloConsumer } from 'react-apollo';
import { AuthConsumer, AuthProviderValue } from './AuthContext';

interface State {
    error?: Error;
}

export const errorNotFound = new Error('Not Found');
export const errorAccessDenied = new Error('Access Denied');
export const unauthenticated = new Error('Unauthenticated');
export const errorServerError = new Error('Server Error');

interface Props extends AuthProviderValue {
    client: ApolloClient<any>;
}

class ErrorBoundary extends React.Component<Props, State> {
    state: State = {};

    resetStore = async () => {
        await this.props.client.resetStore();
    };

    componentDidCatch(error: Error) {
        this.setState({ error });

        if (error === unauthenticated) {
            this.props
                .login({
                    showLogin: true,
                    isDismissable: false,
                    postLogin: this.resetStore,
                })
                .then(success => {
                    if (!success) throw 'Required authentication failed';
                    this.setState({ error: undefined });
                }, onReject('ErrorBoundary::unauthenticated'));
        }
    }

    render() {
        const { error } = this.state;

        if (error === unauthenticated) {
            return null;
        }

        if (error === errorNotFound) {
            return (
                <ErrorPage
                    error={error}
                    heading={__('Lost your way?')}
                    message={__('This is not where I want to be!')}
                />
            );
        }

        if (error === errorAccessDenied) {
            return (
                <ErrorPage
                    error={error}
                    heading={__('Access denied')}
                    message={__("You don't have permission to access this page.")}
                />
            );
        }

        if (error) {
            // Skip logging GraphQL errors as they are already logged in more detail at the apollo-link level
            if (!isApolloError(error)) {
                Bugsnag.notify(error);
            }

            return (
                <ErrorPage
                    error={error}
                    heading={__('Eeeeeeek!')}
                    message={__(
                        'It seems there was a problem. The 99designs development team has been notified and are likely addressing the problem at this very minute. Sit tight, grab a coffee, and hit refresh in a few minutes.'
                    )}
                />
            );
        }

        return this.props.children;
    }
}

export default function WrappedErrorBoundary({ children }: { children: React.ReactNode }) {
    return (
        <AuthConsumer>
            {(value: AuthProviderValue) => (
                <ApolloConsumer>
                    {client => (
                        <ErrorBoundary {...value} client={client}>
                            {children}
                        </ErrorBoundary>
                    )}
                </ApolloConsumer>
            )}
        </AuthConsumer>
    );
}
